import { useCallback, useState } from "react";
import {
  Box,
  Flex,
  FormControl,
  HStack,
  IconButton,
  Input,
  ListItem,
  Text,
  UnorderedList,
  VStack,
} from "@chakra-ui/react";
import { useDropzone } from "react-dropzone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ACCEPTED_FILE_TYPES } from "utils/constants";
import { Link } from "react-router-dom";
import {
  isApplicable as isSowApplicable,
  SowFileTemplate,
} from "./fixed-documents/sow-file-template";
import {
  isApplicable as isTrackRecordApplicable,
  TrackRecordFileTemplate,
} from "./fixed-documents/track-record-file-template";

interface FileListProps {
  files: any[];
  setFiles: (files: any[]) => void;
  item?: {
    category: string;
    title: string;
    type: string;
  };
}

export const FilesList = ({ files, setFiles, item }: FileListProps) => {
  if (files.length === 0 && !isSowApplicable(item) && !isTrackRecordApplicable(item)) {
    return null;
  }

  return (
    <UnorderedList listStyleType="none" p="0" m="0" mb="20px">
      <SowFileTemplate item={item} />
      <TrackRecordFileTemplate item={item} />

      {files.map((file, index) => (
        <ListItem
          key={`${file.size + index}`}
          py="12px"
          borderColor="gray.50"
          borderBottomWidth="1px"
        >
          <Flex justifyContent="space-between" fontSize="14px" alignItems="center">
            <Link to={`/documents/${file.id}`}>{file.name}</Link>
            <Flex alignItems="center">
              <Text>{((file.size || file.file_size) / 1024 / 1024).toFixed(1)}MB</Text>
              <IconButton
                width="9px"
                height="9px"
                aria-label="remove-file"
                variant="ghost"
                onClick={() => setFiles(files.filter((_, i) => index !== i))}
                icon={<FontAwesomeIcon icon={["fal", "times"]} />}
              />
            </Flex>
          </Flex>
        </ListItem>
      ))}
    </UnorderedList>
  );
};

const DocumentsUpload = ({
  files = [],
  setFiles,
  maxFiles = 10,
  showFileSize = false,
}: {
  files: any[];
  setFiles: (files: any[]) => void;
  maxFiles?: number;
  showFileSize?: boolean;
}) => {
  const [docName, setDocName] = useState("");
  const [editingDocId, setEditingDocId] = useState<string | null>(null);

  const onDrop = useCallback(
    (acceptedFiles: any[]) => {
      setFiles(acceptedFiles.concat(files));
    },
    [files, setFiles]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    maxFiles,
    accept: ACCEPTED_FILE_TYPES,
  });

  const handleSaveName = (index: number) => {
    const updatedFiles = files.map((file, i) => {
      if (i === index) {
        return new File([file], docName, {
          type: file.type,
          lastModified: file.lastModified,
        });
      }
      return file;
    });
    setFiles(updatedFiles);
    setEditingDocId(null);
    setDocName("");
  };

  return (
    <Box>
      {maxFiles && files.length < maxFiles && (
        <Box>
          <Box
            borderWidth="2px"
            borderColor="primary"
            borderRadius="10px"
            padding="28px"
            mb="14px"
            {...getRootProps()}
          >
            <input {...getInputProps()} />
            <VStack color="primary" fontSize="36px">
              <FontAwesomeIcon icon={["far", "upload"]} />
              <Text fontWeight="500" color="secondary" fontSize="15px">
                {isDragActive
                  ? `Drag & drop or click to upload ${maxFiles > 1 ? "files" : "a file."}`
                  : `Drop the file${maxFiles > 1 ? "s" : ""} here...`}
              </Text>
            </VStack>
          </Box>
          <HStack justifyContent="space-between" fontSize="13px" mb="40px">
            <Text>Accepted file types: .pdf, .jpg, .png, .csv </Text>
            <Text>Maximum file size: 500 MB </Text>
          </HStack>
        </Box>
      )}

      {files.length > 0 && (
        <UnorderedList
          listStyleType="none"
          p="0"
          m="0"
          mb="20px"
          borderBottomColor="#E2E2E2"
          borderBottomWidth="1px"
        >
          {files.map((file, index) => (
            <ListItem key={file.size} py="12px" borderColor="gray.50" borderBottomWidth="1px">
              <Flex justifyContent="space-between" fontSize="14px" alignItems="center">
                <IconButton
                  border={"1px solid #D4D4D4"}
                  borderRadius={"8px"}
                  p={"20px"}
                  width="9px"
                  height="9px"
                  aria-label="edit-file"
                  variant="ghost"
                  onClick={() => {
                    setEditingDocId(file.path);
                    setDocName(file.name);
                  }}
                  icon={<FontAwesomeIcon icon={["fal", "pencil"]} />}
                />
                <Text w={"60%"} ml={"-30px"}>
                  {file?.name}
                </Text>
                <Flex alignItems="center">
                  {showFileSize && (
                    <Text>
                      {((file.size || file.file_size) / 1024 / 1024).toFixed(1)}
                      MB
                    </Text>
                  )}
                  <IconButton
                    ml={"8px"}
                    border={"1px solid #D4D4D4"}
                    borderRadius={"8px"}
                    p={"20px"}
                    width="9px"
                    height="9px"
                    aria-label="remove-file"
                    variant="ghost"
                    onClick={() => setFiles(files.filter((_, i) => index !== i))}
                    icon={<FontAwesomeIcon icon={["fal", "times"]} />}
                  />
                </Flex>
              </Flex>

              {editingDocId === file.path && (
                <FormControl isRequired mt={"15px"}>
                  <Input
                    w={"79%"}
                    variant="primary"
                    placeholder="Edit document name"
                    value={docName}
                    onChange={ev => setDocName(ev.target.value)}
                  />
                  <IconButton
                    ml="10px"
                    border="1px solid #D4D4D4"
                    borderRadius="8px"
                    p="20px"
                    width="9px"
                    height="9px"
                    aria-label="save-file"
                    variant="ghost"
                    onClick={() => handleSaveName(index)}
                    icon={<FontAwesomeIcon icon={["fal", "check"]} />}
                  />
                  <IconButton
                    ml="10px"
                    border="1px solid #D4D4D4"
                    borderRadius="8px"
                    p="20px"
                    width="9px"
                    height="9px"
                    aria-label="cancel-edit"
                    variant="ghost"
                    onClick={() => {
                      setEditingDocId(null);
                      setDocName("");
                    }}
                    icon={<FontAwesomeIcon icon={["fal", "times"]} />}
                  />
                </FormControl>
              )}
            </ListItem>
          ))}
        </UnorderedList>
      )}
    </Box>
  );
};

export default DocumentsUpload;
